import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Divider, Typography } from '@mui/material';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { Cross as Hamburger } from 'hamburger-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useSSR from 'use-ssr';
import { imageUrl } from '../../utils';
import { RoundedArrowButton } from '../buttons';
import NavbarDropDown from './NavbarDropDown';

const linksMap: any = {
  Home: '/',
  About: '/about',
  Projects: '/projects', 
  'Contact Us': '/contact-us'
}

const servicesPaths = ['/web-app-development', '/mobile-app-development', '/ui-ux', '/software-consultation'];

const coloredLogo = imageUrl('atini-logo-colored.svg');
const navbarMotion = {
  rest: { ease: 'easeOut', duration: 0.2, type: 'tween', x: '100vw' },
  drawer: {
    x: 0,
    transition: {
      duration: 0.2,
      type: 'tween',
      ease: 'easeIn',
    },
  },
};
const serviceCategories = [
  { label: 'Web App Development', path: '/web-app-development' },
  { label: 'Mobile App Development', path: '/mobile-app-development' },
  { label: 'UI/UX', path: '/ui-ux' },
  { label: 'Software Consultation', path: '/software-consultation' }
]


const Navbar = () => {
  
  const { isBrowser } = useSSR();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const commonNavlinkClasses = 'justify-center hover:underline underline-offset-8 decoration-yellow decoration-2 text-center';
  const navigate = useNavigate();
  const pathname = isBrowser ? window.location.pathname : (globalThis as any).SSR_PATHNAME;

  const TwLink = ({ text }: { text: string }) => {
    return (
      <div
        className={classNames('cursor-pointer py-2 hover:bg-yellow', pathname === linksMap[text] && 'bg-yellow')}
        onClick={() => {
          setDrawerOpen(false);
        }}>
        <Typography variant="button">{text}</Typography>
      </div>
    );
  };

  useEffect(() => {
    let handleResize = (e: any) => {
      setDrawerOpen(false);
      setServicesOpen(false);
    };
    if(isBrowser) {
      window?.addEventListener('resize', handleResize);
    }
    return () => {
      (isBrowser) && window?.removeEventListener('resize', handleResize);
      setDrawerOpen(false);
      setServicesOpen(false);

    };
  }, []);

  useEffect(() => {
    if (!isBrowser) return;
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isBrowser]);

  const handleNavlinkClick = (path: string) => {
    navigate(path)
    setDrawerOpen(false);
    setServicesOpen(false);
  }

  return (
    <div
      className={classNames(
        'fixed top-0 left-0 right-0 z-50 w-screen transition-[background-color,box-shadow,backdrop-filter] duration-300 ease-out',
        scrolled ? 'bg-white/85 backdrop-blur-md shadow-[0_1px_12px_rgba(38,50,56,0.08)]' : 'bg-white',
      )}>
      <div
        className={classNames(
          'flex items-center justify-center mdmax:justify-between px-1 sm:px-3 relative transition-[height] duration-300 ease-out',
          scrolled ? 'h-[4.75rem] md:h-[5.5rem]' : 'h-[7rem] md:h-navbarHeight',
        )}>
        <div className="flex justify-center items-center">
          <div className="hidden mdmax:flex">
            <Hamburger toggled={drawerOpen} onToggle={setDrawerOpen} size={22} />
          </div>
          {/* this is for logo image */}
          <Link to={'/'}>
            <div className="cursor-pointer w-[69.57px] h-[78px] flex items-center justify-center">
              <img
                className={classNames(
                  'transition-[height] duration-300 ease-out',
                  scrolled ? 'h-[56px]' : 'h-[calc(theme(space.navbarHeight)-50px)]',
                )}
                src={coloredLogo}
                alt="logo"
              />
            </div>
          </Link>
        </div>
        {/* this is for logo image end */}

        {/* this is for the nav links present */}
        <div className="flex w-full max-w-[737px] justify-between breakonemin:px-[64px] px-[30px] mdmax:hidden">
          <Link to="/">
            <div className={classNames('cursor-pointer', commonNavlinkClasses, pathname === '/' && 'underline')}>
              <Typography style={{fontFamily: 'var(--fontPrimary)'}} variant="button">Home</Typography>
            </div>
          </Link>
          <Link to="/about">
            <div className={classNames('cursor-pointer', commonNavlinkClasses, pathname === '/about' && 'underline')}>
              <Typography style={{fontFamily: 'var(--fontPrimary)'}} variant="button">About</Typography>
            </div>
          </Link>
          <NavbarDropDown
            label={
              <div className={classNames('cursor-pointer', commonNavlinkClasses, ['/web-app-development', '/mobile-app-development', '/ui-ux', '/software-consultation'].includes(pathname) && 'underline')}>
                <Typography style={{fontFamily: 'var(--fontPrimary)'}} variant="button">Services<ExpandMoreIcon sx={{ width: 18, marginLeft: 0.5 }} /></Typography>
              </div>}
            subLabels={serviceCategories}
          />
          <Link to="/projects">
            <div className={classNames('cursor-pointer', commonNavlinkClasses, pathname === '/projects' && 'underline')}>
              <Typography variant="button">Projects</Typography>
            </div>
          </Link>
          {/* TODO: Put back blog */}
          <Link to="/blogs">
              <div className={classNames('cursor-pointer', commonNavlinkClasses)}>
                <Typography variant="button">Blogs</Typography>
              </div>
            </Link>
        </div>
        <RoundedArrowButton
          text={'Contact Us'}
          arrowContainerClasses="ml-10"
          containerClasses="mdmax:hidden"
          onClick={() => navigate('/contact-us')}
        />
        <div className="hidden lgmax:flex lgmax:items-center lgmax:gap-2">
          <div className="md:hidden">
            <RoundedArrowButton text={'Contact Us'} containerClasses="mr-2" arrowContainerClasses="ml-10" onClick={() => navigate('/contact-us')}></RoundedArrowButton>
          </div>
          <AnimatePresence>
            {drawerOpen && (
              <motion.div
                className="flex flex-col text-center absolute left-0 right-0 top-full min-w-[300px] bg-gray-100 py-4 gap-2 rounded-lg mx-10"
                variants={navbarMotion}
                initial={{ x: -500 }}
                animate="drawer"
                exit="rest">
                <>
                  <Link
                    to="/"
                    onClick={() => handleNavlinkClick('/')}
                  >
                    <TwLink text={'Home'} />
                  </Link>
                  <Link
                    to="/about"
                    onClick={() => handleNavlinkClick('/about')}

                  >
                    <TwLink text={'About'} />
                  </Link>
                  <div
                    className={classNames('cursor-pointer py-2 hover:bg-yellow', servicesPaths.includes(pathname) && 'bg-yellow')}
                    onClick={() => setServicesOpen(prev => !prev)}
                  >
                    <Typography sx={{ position: 'relative' }} variant="button">{'Services'}<ExpandMoreIcon sx={{ width: 14, position: 'absolute', top: -1, right: -16 }} /></Typography>
                  </div>
                  {servicesOpen && (
                    <>
                      <Divider />
                      <div className="flex flex-col mx-4">
                        {serviceCategories.map((service: any) => (
                          <Link key={service.path} to={service.path} onClick={() => handleNavlinkClick(service.path)}>
                            <div
                              className="cursor-pointer py-2 hover:bg-yellow"
                              onClick={() => {
                                setDrawerOpen(false);
                              }}>
                              <Typography fontSize={16} variant="button">  {service.label}</Typography>
                            </div>
                          </Link>
                        ))}
                      </div>
                      <Divider />
                    </>
                  )}
                  {/* TODO: Put back blog */}
                  {/* <Link
                      to="/blogs"
                    >
                      <TwLink>
                        <Typography variant="button">{'Blog'}</Typography>
                      </TwLink>
                    </Link> */}
                  <Link
                    to="/projects"
                    onClick={() => handleNavlinkClick('/projects')}
                  >
                    <TwLink text={'Projects'} />
                  </Link>
                  <Link
                    to="/blogs"
                    onClick={() => handleNavlinkClick('/blogs')}
                  >
                    <TwLink text={'Blogs'} />
                  </Link>
                  
                  <Link
                    to="/contact-us"
                  >
                    <TwLink text={'Contact Us'} />
                  </Link>
                </>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Navbar;